import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Radio, Checkbox, Alert } from 'antd';
import { MCaptchaWidget, INPUT_NAME } from '@mcaptcha/react-glue';
import { Link } from "react-router-dom";
import { useLaborUnions } from '../../../hooks/useLaborUnions';
import {
  getNameRules,
  getFunctionRules,
  getPhoneRules,
  getEmailRules,
  getZipcodeRules,
  getRequiredTextRules,
} from '../../helper-function/validationRules';

const { Option } = Select;

/**
 * Slide 3 ADH : Représentant légal + Référent APAS + Infos adhérent + Captcha
 */
const StepAdhDetails = ({ onNext, onPrev, onSubmit, loading, companyPrefill }) => {
  const [form] = Form.useForm();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isAgreeOptWorkHidden, setIsAgreeOptWorkHidden] = useState(true);

  // Vérifier si le captcha est activé via variable d'environnement
  const isCaptchaEnabled = process.env.REACT_APP_ENABLE_CAPTCHA === 'true';

  // Récupération dynamique des syndicats patronaux depuis l'API
  const { laborUnionsSelectOptions, loading: laborUnionsLoading } = useLaborUnions();

  // Configuration du widget mCaptcha
  // Utiliser widgetLink au lieu de siteKey pour spécifier l'URL complète
  const widgetUrl = `${process.env.REACT_APP_MCAPTCHA_INSTANCE_URL}/widget/?sitekey=${process.env.REACT_APP_MCAPTCHA_SITE_KEY}`;
  const mcaptchaConfig = {
    widgetLink: new URL(widgetUrl),
  };

  // Pré-remplir les champs d'adresse avec les données de la company (saisies par l'admin)
  useEffect(() => {
    if (companyPrefill) {
      form.setFieldsValue({
        address1: companyPrefill.address1 || '',
        address2: companyPrefill.address2 || '',
        address3: companyPrefill.address3 || '',
        address4: companyPrefill.address4 || '',
        zipcode: companyPrefill.zipcode || '',
        city: companyPrefill.city || '',
      });
    }
  }, [companyPrefill, form]);

  // Écouter les changements du champ caché mCaptcha pour récupérer le token
  useEffect(() => {
    // Ne pas extraire le token si le captcha est désactivé
    if (!isCaptchaEnabled) {
      return;
    }

    const checkToken = setInterval(() => {
      const tokenInput = document.querySelector(`input[name="${INPUT_NAME}"]`);
      if (tokenInput && tokenInput.value) {
        setCaptchaToken(tokenInput.value);
        clearInterval(checkToken);
      }
    }, 500);

    return () => clearInterval(checkToken);
  }, [isCaptchaEnabled]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Vérifier le captcha uniquement s'il est activé
      if (isCaptchaEnabled && !captchaToken) {
        alert('Veuillez valider le captcha');
        return;
      }

      // Ajouter le token captcha uniquement s'il est activé
      const finalData = {
        ...values,
        ...(isCaptchaEnabled && { captchaToken: captchaToken }),
      };

      // Appeler la fonction de soumission finale
      onSubmit(finalData);
    } catch (err) {
      // Erreurs de validation affichées par Ant Design
    }
  };

  // Gérer la visibilité du champ agreeOptionalWorker
  const handleFieldsChange = (changedFields, allFields) => {
    const laborUnionValue = form.getFieldValue('laborUnion');
    const workForceValue = form.getFieldValue('workForce');

    // Afficher agreeOptionalWorker UNIQUEMENT si :
    // - Les deux champs sont renseignés
    // - ET laborUnion !== 2 (pas CAPEB)
    // - ET workForce === 'moreThan10'
    const shouldHide = !laborUnionValue || !workForceValue || laborUnionValue === 2 || workForceValue === 'upTo10';
    setIsAgreeOptWorkHidden(shouldHide);

    // Si masqué, on met la valeur à false
    if (shouldHide) {
      form.setFieldsValue({ agreeOptionalWorker: false });
    }
  };

  return (
    <div className="step-adh-details">
      <Alert
        message="Étape 3 : Informations de l'entreprise adhérente"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={handleFieldsChange}
        autoComplete="off"
        initialValues={{
          buildingConventionOutside77: true,
          agreeOptionalWorker: false,
        }}
      >
        {/* Adresse de l'entreprise */}
        <h4>Adresse de l'entreprise</h4>

        <Form.Item
          label="Complément d'adresse (optionnel)"
          name="address1"
          rules={[{ max: 255, message: "Maximum 255 caractères" }]}
          autoComplete="new-password"
        >
          <Input placeholder="Bâtiment, étage, appartement..." size="large" />
        </Form.Item>

        <Form.Item
          label="Adresse – ligne 1"
          name="address2"
          rules={getRequiredTextRules("L'adresse")}
        >
          <Input placeholder="Numéro et nom de rue" size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Adresse – ligne 2 (optionnel)"
          name="address3"
          rules={[{ max: 255, message: "Maximum 255 caractères" }]}
        >
          <Input placeholder="Lieu-dit, hameau..." size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Votre référence (optionnel)"
          name="address4"
          rules={[{ max: 255, message: "Maximum 255 caractères" }]}
        >
          <Input placeholder="Référence interne" size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Code postal"
          name="zipcode"
          rules={getZipcodeRules(true)}
        >
          <Input placeholder="75000" size="large" maxLength={5} autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Commune"
          name="city"
          rules={getRequiredTextRules("La commune")}
        >
          <Input placeholder="Paris" size="large" autoComplete="new-password" />
        </Form.Item>

        {/* Représentant légal */}
        <h4 style={{ marginTop: 32 }}>Représentant légal</h4>

        <Form.Item
          label="Nom"
          name="representativeLastname"
          rules={getNameRules("Le nom du représentant")}
        >
          <Input placeholder="Nom du représentant" size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Prénom"
          name="representativeFirstname"
          rules={getNameRules("Le prénom du représentant")}
        >
          <Input placeholder="Prénom du représentant" size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Fonction"
          name="representativeFunction"
          rules={getFunctionRules("La fonction du représentant")}
        >
          <Input placeholder="Ex: Gérant, Directeur, etc." size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Téléphone"
          name="representativePhone"
          rules={getPhoneRules()}
        >
          <Input placeholder="0123456789" size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Email (optionnel)"
          name="representativeEmail"
          rules={getEmailRules(false)}
        >
          <Input size="large" autoComplete="new-password" />
        </Form.Item>

        {/* Référent APAS */}
        <h4 style={{ marginTop: 32 }}>Référent APAS</h4>
        <Alert
          message="Interlocuteur relais entre l'entreprise, les salariés et l'APAS-BTP.
            Rôle : recevoir les informations de l'association et les diffuser auprès des salariés, orienter les salariés vers l'association si besoin, faire remonter les questions ou besoins identifiés."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form.Item
          label="Nom"
          name="referentApasLastname"
          rules={getNameRules("Le nom du référent APAS")}
        >
          <Input placeholder="Nom du référent" size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Prénom"
          name="referentApasFirstname"
          rules={getNameRules("Le prénom du référent APAS")}
        >
          <Input placeholder="Prénom du référent" size="large" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label="Email"
          name="referentApasEmail"
          rules={getEmailRules()}
        >
          <Input size="large" autoComplete="new-password" />
        </Form.Item>

        {/* Informations adhérent */}
        <h4 style={{ marginTop: 32 }}>Informations adhérent</h4>

        <Form.Item
          label="Organisation professionnelle"
          name="laborUnion"
          rules={[{ required: true, message: 'Veuillez sélectionner une organisation' }]}
        >
          <Select
            placeholder="Sélectionnez une organisation"
            size="large"
            loading={laborUnionsLoading}
            disabled={laborUnionsLoading}
          >
            {laborUnionsSelectOptions.map((union) => (
              <Option key={union.id} value={union.id}>
                {union.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Effectifs"
          name="workForce"
          rules={[{ required: true, message: 'Veuillez sélectionner les effectifs' }]}
        >
          <Radio.Group size="large">
            <Radio value="upTo10">Jusqu'à 10 salariés</Radio>
            <Radio value="moreThan10">Plus de 10 salariés</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="buildingConventionOutside77"
          valuePropName="checked"
          extra="Case à décocher si l'Adhérent est Volontaire ou situé dans le 77"
        >
          <Checkbox>
            Conventions du Bâtiment hors 77
          </Checkbox>
        </Form.Item>

        {!isAgreeOptWorkHidden && (
          <Form.Item
            name="agreeOptionalWorker"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => {
                  const wf = form.getFieldValue('workForce');
                  const lu = form.getFieldValue('laborUnion');
                  return (value && wf === 'moreThan10') ||
                    wf === 'upTo10' ||
                    lu === 2
                    ? Promise.resolve()
                    : Promise.reject(new Error('Veuillez valider avoir pris connaissance'));
                },
              },
            ]}
          >
            <Checkbox>
              J'ai pris connaissance que la souscription aux Prestations pour les Ouvriers est optionnelle
            </Checkbox>
          </Form.Item>
        )}

        {/* Conditions */}
        <h4 style={{ marginTop: 32 }}>Conditions d'utilisation</h4>

        <Form.Item
          name="agreeTermsServiceMember"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error('Vous devez accepter les conditions d\'utilisation')),
            },
          ]}
        >
          <Checkbox>
            J'ai lu et accepte{" "}
            <Link
              to="/charte-adherent"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "blue" }}
            >
              la charte de l'adhérent
            </Link>
          </Checkbox>
        </Form.Item>

        {/* Captcha */}
        {isCaptchaEnabled && (
          <Form.Item style={{ marginTop: 24 }}>
            <MCaptchaWidget {...mcaptchaConfig} />
          </Form.Item>
        )}

        {/* Boutons de navigation */}
        <Form.Item style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={onPrev} size="large" disabled={loading}>
              Précédent
            </Button>
            <Button type="primary" htmlType="submit" size="large" loading={loading}>
              Finaliser l'inscription
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default StepAdhDetails;
